<template>
  <div class="devices-franquias-form">
    <!-- General Information Box -->
    <box
      type="primary"
      :title="t('Informações gerais')"
      elem-id="boxInformacoesGeraisDevice"
      :validation="() => generalValidationErrors.length > 0">

      <!-- Provider Type Selector (only for new records) -->
      <div v-if="!isEditMode" class="form-group" data-testid="provider-type-selector">
        <select-list
          :label="t('Tipo de Dispositivo')"
          :placeholder="t('Selecione o tipo')"
          :itens-select="providerTypeOptions"
          :validation="() => true"
          :ng-required="true"
          elem-id="provider_type"
          data-testid="devices-franquias-provider-type-select"
          v-model="deviceData.provider_type">
        </select-list>
      </div>

      <!-- Provider type badge (edit mode only) -->
      <div v-if="isEditMode" class="form-group">
        <label class="form-label">{{ t('Tipo de Dispositivo') }}</label>
        <div>
          <span :class="['provider-badge', deviceData.provider_type === 'instagram' ? 'provider-badge--instagram' : 'provider-badge--whatsapp']">
            <i :class="['pi', deviceData.provider_type === 'instagram' ? 'pi-instagram' : 'pi-whatsapp']"></i>
            {{ deviceData.provider_type === 'instagram' ? 'Instagram' : 'WhatsApp' }}
          </span>
        </div>
      </div>

      <div class="form-group">
        <select-list-multiple
          :label="t('Franquias')"
          :placeholder="t('Selecione as franquias')"
          :itens-select="franquiasOptions"
          :validation="() => isFranquiasValid"
          :ng-required="true"
          elem-id="franquia_ids"
          data-testid="devices-franquias-franquias-select"
          v-model="deviceData.franquia_ids"
          display="chip"
          :max-selected-labels="3"
          :show-clear="true">
        </select-list-multiple>
      </div>

      <!-- WhatsApp-specific fields -->
      <template v-if="isWhatsappProvider" data-testid="whatsapp-fields">
        <div class="form-group">
          <input-text
            :label="t('Número WhatsApp')"
            :placeholder="t('Número WhatsApp')"
            :validation="() => isNumeroWhatsappValid"
            :required="true"
            elem-id="numero_whatsapp"
            data-testid="devices-franquias-numero-whatsapp-input"
            v-model="deviceData.numero_whatsapp">
          </input-text>
        </div>

        <div class="form-group">
          <input-text
            :label="t('Nome do Dispositivo')"
            :placeholder="t('Nome do Dispositivo')"
            :validation="() => true"
            :required="false"
            elem-id="device_name"
            data-testid="devices-franquias-device-name-input"
            v-model="deviceData.device_name">
          </input-text>
        </div>

        <div class="form-group">
          <input-text
            :label="t('Device Token')"
            :placeholder="t('Device Token')"
            :validation="() => true"
            :required="false"
            :disabled="true"
            elem-id="device_token"
            data-testid="devices-franquias-device-token-input"
            v-model="deviceData.device_token">
          </input-text>
        </div>
      </template>

      <!-- Instagram info message (new mode) -->
      <div v-if="!isWhatsappProvider && !isEditMode" class="instagram-info" data-testid="instagram-info">
        <i class="pi pi-info-circle"></i>
        <span>{{ t('Contas Instagram são configuradas na página de Contas Instagram. Após conectar a conta, ela aparecerá automaticamente na lista de dispositivos.') }}</span>
      </div>
    </box>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, reactive, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { useFiltro } from '../../composables/useFiltro';
import { useDevicesFranquias } from '../../composables/useDevicesFranquias';
import { log } from '../../utils/DebugLogger';
import SelectListMultiple from '../../inputs/SelectListMultiple.vue';

type DeviceId = number;

interface SelectOption {
  ID: string | number;
  Name: string;
}

interface DeviceData {
  device_id: DeviceId;
  provider_type: string;
  franquia_ids: (string | number)[];
  numero_whatsapp: string;
  device_name: string;
  device_token: string;
}

interface SaveSuccessPayload {
  device_id: DeviceId;
  device_name: string;
  provider_type: string;
}

withDefaults(defineProps<{
  isEditMode?: boolean;
}>(), {
  isEditMode: false
});

const emit = defineEmits<{
  'save-success': [payload: SaveSuccessPayload];
}>();

// Get i18n composable
const { t } = useI18n();

// Get filtro composable
const { getSelectedFranchisesSelectArray } = useFiltro();

// Get whatsapp franquias composable
const { fetchDevice, saveDevice } = useDevicesFranquias();

// Reactive data
const isLoading = ref(false);

const deviceData = reactive<DeviceData>({
  device_id: 0,
  provider_type: 'whatsapp',
  franquia_ids: [],
  numero_whatsapp: '',
  device_name: '',
  device_token: ''
});

const franquiasOptions = ref<SelectOption[]>([]);

const providerTypeOptions = ref<SelectOption[]>([
  { ID: 'whatsapp', Name: 'WhatsApp (Evolution API)' },
  { ID: 'instagram', Name: 'Instagram DM' }
]);

// Computed properties
const isWhatsappProvider = computed(() => {
  return deviceData.provider_type === 'whatsapp';
});

const isFranquiasValid = computed(() => {
  return deviceData.franquia_ids && deviceData.franquia_ids.length > 0;
});

const isNumeroWhatsappValid = computed(() => {
  if (!isWhatsappProvider.value) return true;
  return deviceData.numero_whatsapp && deviceData.numero_whatsapp.trim() !== '';
});

const generalValidationErrors = computed(() => {
  const errors = [];

  if (!isFranquiasValid.value) {
    errors.push(t('Você deve informar pelo menos uma franquia válida'));
  }

  if (isWhatsappProvider.value && !isNumeroWhatsappValid.value) {
    errors.push(t('Você deve informar um número de WhatsApp válido'));
  }

  return errors;
});

const canSave = computed(() => {
  return generalValidationErrors.value.length === 0;
});

// Methods
const loadFranquias = async (): Promise<void> => {
  log('DevicesFranquiasForm', 'loadFranquias', 'Loading franchises from filter');

  try {
    // Get only the franchises selected in the filter
    const franquiasArray = await getSelectedFranchisesSelectArray();

    // Map to the format expected by the multi-select
    franquiasOptions.value = franquiasArray.map((franquia: SelectOption) => ({
      ID: franquia.ID,
      Name: franquia.Name
    }));

    log('DevicesFranquiasForm', 'loadFranquias', 'Franchises loaded from filter', franquiasOptions.value);
  } catch (error) {
    log('DevicesFranquiasForm', 'loadFranquias', 'Error loading franchises', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar franquias'));
  }
};

const initializeForNew = async (): Promise<void> => {
  log('DevicesFranquiasForm', 'initializeForNew', 'Initializing form for new record');

  // Reset form data
  deviceData.device_id = 0;
  deviceData.provider_type = 'whatsapp';
  deviceData.franquia_ids = [];
  deviceData.numero_whatsapp = '';
  deviceData.device_name = '';
  deviceData.device_token = '';

  // Load franchises
  await loadFranquias();
};

const loadForEdit = async (deviceId: DeviceId): Promise<void> => {
  log('DevicesFranquiasForm', 'loadForEdit', 'Loading data for edit', deviceId);
  isLoading.value = true;

  try {
    // Load franchises first
    await loadFranquias();

    // Load device data
    const data = await fetchDevice(deviceId);
    if (!data) return;

    deviceData.device_id = data.device_id || 0;
    deviceData.provider_type = data.provider_type || 'whatsapp';
    deviceData.numero_whatsapp = data.numero_whatsapp || '';
    deviceData.device_name = data.device_name || '';
    deviceData.device_token = data.device_token || '';
    deviceData.franquia_ids = data.franquia_ids || [];

    log('DevicesFranquiasForm', 'loadForEdit', 'Data loaded', deviceData);
  } catch (error) {
    log('DevicesFranquiasForm', 'loadForEdit', 'Error loading data', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar dados'));
  } finally {
    isLoading.value = false;
  }
};

const save = async (): Promise<void> => {
  log('DevicesFranquiasForm', 'save', 'Saving device', deviceData);

  if (!canSave.value) {
    eventBus.emit('globalMessage.error', t('Por favor, preencha todos os campos obrigatórios'));
    return;
  }

  isLoading.value = true;

  try {
    const payload = {
      device_id: deviceData.device_id,
      provider_type: deviceData.provider_type,
      numero_whatsapp: deviceData.numero_whatsapp,
      device_name: deviceData.device_name,
      device_token: deviceData.device_token,
      franquia_ids: deviceData.franquia_ids.map(Number)
    };

    const response = (await saveDevice(payload)) as { dados?: { device_id?: DeviceId; whatsappfranquia_id?: DeviceId } } | undefined;
    const savedId: DeviceId = response?.dados?.device_id || response?.dados?.whatsappfranquia_id || payload.device_id;

    log('DevicesFranquiasForm', 'save', 'Save successful', { savedId });
    emit('save-success', { device_id: savedId, device_name: payload.device_name, provider_type: payload.provider_type });
  } catch (error) {
    log('DevicesFranquiasForm', 'save', 'Error saving data', error);
    eventBus.emit('globalMessage.error', t('Erro ao salvar registro'));
  } finally {
    isLoading.value = false;
  }
};

const resetForm = (): void => {
  log('DevicesFranquiasForm', 'resetForm', 'Resetting form');

  deviceData.device_id = 0;
  deviceData.provider_type = 'whatsapp';
  deviceData.franquia_ids = [];
  deviceData.numero_whatsapp = '';
  deviceData.device_name = '';
  deviceData.device_token = '';
};

// Lifecycle
onMounted(() => {
  log('DevicesFranquiasForm', 'mounted', 'Component mounted');
});

// Expose methods for parent components
defineExpose({
  initializeForNew,
  loadForEdit,
  save,
  resetForm,
  canSave
});
</script>

<style scoped>
.devices-franquias-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-label {
  display: block;
  font-weight: 600;
  margin-bottom: 8px;
  font-size: 14px;
}

.provider-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
}

.provider-badge--whatsapp {
  color: #22c55e;
  background: #f0fdf4;
}

.provider-badge--instagram {
  color: #a855f7;
  background: #faf5ff;
}

.instagram-info {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 16px;
  background: #faf5ff;
  border: 1px solid #e9d5ff;
  border-radius: 8px;
  color: #7c3aed;
  font-size: 14px;
  line-height: 1.5;
}

.instagram-info i {
  font-size: 18px;
  margin-top: 2px;
}
</style>
